import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, HelpCircle, X } from 'lucide-react';

export interface ToolbarDropdownItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  loading?: boolean;
  hidden?: boolean;
  helpContent?: {
    title: string;
    body: React.ReactNode;
  };
  divider?: boolean;
}

interface ToolbarDropdownProps {
  label: string;
  icon: React.ReactNode;
  items: ToolbarDropdownItem[];
  /** When true, dropdown stays open after clicking items (for toggles). Default: false */
  keepOpenOnClick?: boolean;
  /** Show a small indicator dot when any item is active */
  showActiveIndicator?: boolean;
  /** Optional header help content shown at top of dropdown */
  headerHelp?: {
    title: string;
    body: React.ReactNode;
  };
  className?: string;
  /** Position dropdown menu relative to button */
  dropdownPosition?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  /**
   * Render the menu into document.body with fixed positioning instead of
   * absolutely inside the trigger. Opt-in, for call sites where an ancestor
   * clips or paints over the menu — e.g. the validation bar in the page header,
   * which sits inside Layout's scrolling <main>. Existing toolbar call sites
   * leave this off and are unaffected.
   */
  portalMenu?: boolean;
}

const ToolbarDropdown: React.FC<ToolbarDropdownProps> = ({
  label,
  icon,
  items,
  keepOpenOnClick = false,
  showActiveIndicator = false,
  headerHelp,
  className = '',
  dropdownPosition = 'bottom-left',
  portalMenu = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeHelpKey, setActiveHelpKey] = useState<string | null>(null);
  const [showHeaderHelp, setShowHeaderHelp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ left: number; top: number } | null>(null);

  const hasActiveItem = items.some(item => item.active && !item.hidden);
  const activeItems = items.filter(item => item.active && !item.hidden);

  // Click-outside detection
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        // the portalled menu lives outside containerRef, so check it separately
        !(menuRef.current && menuRef.current.contains(event.target as Node))
      ) {
        setIsOpen(false);
        setActiveHelpKey(null);
        setShowHeaderHelp(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleItemClick = useCallback((item: ToolbarDropdownItem) => {
    if (item.disabled || item.loading) return;
    item.onClick();
    if (!keepOpenOnClick) {
      setIsOpen(false);
      setActiveHelpKey(null);
      setShowHeaderHelp(false);
    }
  }, [keepOpenOnClick]);

  const handleHelpClick = useCallback((e: React.MouseEvent, key: string) => {
    e.stopPropagation();
    setActiveHelpKey(prev => prev === key ? null : key);
    setShowHeaderHelp(false);
  }, []);

  const visibleItems = items.filter(item => !item.hidden);

  const positionClasses = {
    'bottom-left': 'top-full left-0 mt-1',
    'bottom-right': 'top-full right-0 mt-1',
    'top-left': 'bottom-full left-0 mb-1',
    'top-right': 'bottom-full right-0 mb-1',
  };

  const MENU_WIDTH = 288; // w-72

  // Anchor the portalled menu to the trigger in viewport coordinates, and keep it
  // there while the page scrolls or resizes underneath it.
  useLayoutEffect(() => {
    if (!portalMenu || !isOpen) {
      setMenuPos(null);
      return;
    }

    const place = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const opensUp = dropdownPosition.startsWith('top');
      const alignsRight = dropdownPosition.endsWith('right');

      let left = alignsRight ? r.right - MENU_WIDTH : r.left;
      left = Math.max(8, Math.min(left, window.innerWidth - MENU_WIDTH - 8));

      const top = opensUp ? r.top - 4 : r.bottom + 4;
      setMenuPos({ left, top });
    };

    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [portalMenu, isOpen, dropdownPosition]);

  const renderMenuInto = (menu: React.ReactNode) =>
    portalMenu ? createPortal(menu, document.body) : menu;

  return (
    <div ref={containerRef} className={`relative inline-block ${className}`}>
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        onClick={() => {
          setIsOpen(!isOpen);
          if (isOpen) {
            setActiveHelpKey(null);
            setShowHeaderHelp(false);
          }
        }}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center space-x-1.5 ${
          isOpen
            ? 'bg-gray-200 text-gray-900'
            : hasActiveItem && showActiveIndicator
              ? 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        {icon}
        <span>{label}</span>
        {showActiveIndicator && hasActiveItem && (
          <span className="flex items-center space-x-1 ml-0.5">
            {activeItems.slice(0, 2).map((item, i) => (
              <span key={item.key} className="bg-blue-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none">
                {item.label.replace(' Heatmap', '').replace(' Chart', '')}
              </span>
            ))}
            {activeItems.length > 2 && (
              <span className="bg-blue-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none">
                +{activeItems.length - 2}
              </span>
            )}
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (portalMenu ? menuPos !== null : true) && renderMenuInto(
        <div
          ref={menuRef}
          style={portalMenu && menuPos
            ? {
                left: menuPos.left,
                ...(dropdownPosition.startsWith('top')
                  ? { bottom: window.innerHeight - menuPos.top }
                  : { top: menuPos.top }),
              }
            : undefined}
          className={portalMenu
            ? 'fixed w-72 bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-[9999]'
            : `absolute ${positionClasses[dropdownPosition]} w-72 bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-50`}
        >
          {/* Header Help */}
          {headerHelp && (
            <div className="px-3 py-2 border-b border-gray-100">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowHeaderHelp(!showHeaderHelp);
                  setActiveHelpKey(null);
                }}
                className="flex items-center space-x-2 text-xs text-gray-500 hover:text-electric-blue transition-colors w-full"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                <span className="font-medium">{headerHelp.title}</span>
              </button>
              {showHeaderHelp && (
                <div className="mt-2 p-3 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                  <div className="text-xs text-gray-700 leading-relaxed">
                    {headerHelp.body}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Menu Items */}
          {visibleItems.map((item, index) => (
            <React.Fragment key={item.key}>
              {item.divider && index > 0 && (
                <div className="my-1 border-t border-gray-100" />
              )}
              <div className="relative">
                <button
                  onClick={() => handleItemClick(item)}
                  disabled={item.disabled}
                  className={`w-full text-left px-3 py-2.5 text-sm flex items-center space-x-3 transition-colors ${
                    item.disabled
                      ? 'text-gray-400 cursor-not-allowed'
                      : item.active
                        ? 'bg-blue-50 text-blue-800 hover:bg-blue-100'
                        : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {/* Icon */}
                  <span className={`flex-shrink-0 ${item.active ? 'text-blue-600' : 'text-gray-500'}`}>
                    {item.loading ? (
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      item.icon
                    )}
                  </span>

                  {/* Label */}
                  <span className="flex-1">{item.label}</span>

                  {/* Active check */}
                  {item.active && !item.loading && (
                    <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  )}

                  {/* Help icon */}
                  {item.helpContent && (
                    <button
                      onClick={(e) => handleHelpClick(e, item.key)}
                      className={`flex-shrink-0 p-0.5 rounded transition-colors ${
                        activeHelpKey === item.key
                          ? 'text-electric-blue'
                          : 'text-gray-400 hover:text-electric-blue'
                      }`}
                      aria-label={`Help: ${item.label}`}
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                    </button>
                  )}
                </button>

                {/* Inline Help Popover */}
                {item.helpContent && activeHelpKey === item.key && (
                  <div className="mx-3 mb-2 p-3 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center space-x-1.5">
                        <HelpCircle className="w-3.5 h-3.5 text-purple-600" />
                        <span className="text-xs font-semibold text-purple-800">Help</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveHelpKey(null);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-xs font-medium text-gray-800 mb-1">{item.helpContent.title}</p>
                    <div className="text-xs text-gray-600 leading-relaxed">
                      {item.helpContent.body}
                    </div>
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default ToolbarDropdown;
